import React, { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { Clip } from '@ff-os/rendering-shared/Clip';
import { sliceCropToAspect } from '@ff-os/shared-utils/crop/sliceCropToSize';
import { Area } from '@ff-os/shared-utils/types/Area';
import { Size } from '@ff-os/shared-utils/types/Size';
import 'react-image-crop/dist/ReactCrop.css';
import classNames from 'classnames';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop/src/index';
import { CropOverlay } from './CropOverlay';
import styles from './CropTool.module.scss';

export const CropTool = ({
    onChange, overlayUrl, filter,
    imageSize, activeCropBox,
    containerSize, clip, aspectRatio,
    children
}: Props) => {

    const [animateControls, setAnimateControls] = useState(true);
    const [crop, setCrop] = useState<Crop>();

    useEffect(() => {

        if (!containerSize) {
            return;
        }

        const cropBox = aspectRatio
            ? sliceCropToAspect(activeCropBox, aspectRatio)
            : activeCropBox;

        setCrop({
            x: containerSize.width / imageSize.width * cropBox.x,
            y: containerSize.height / imageSize.height * cropBox.y,
            width: containerSize.width / imageSize.width * cropBox.width,
            height: containerSize.height / imageSize.height * cropBox.height,
            unit: 'px'
        } as PixelCrop);
    }, [activeCropBox, imageSize, setCrop, containerSize, aspectRatio]);

    const updateCropBox = useCallback((crop: Crop, cropRatio: Crop) => {

        setCrop(crop);

        onChange({
            x: Math.round(imageSize.width / 100 * cropRatio.x),
            y: Math.round(imageSize.height / 100 * cropRatio.y),
            width: Math.round(imageSize.width / 100 * cropRatio.width),
            height: Math.round(imageSize.height / 100 * cropRatio.height)
        });
    }, [imageSize, onChange]);

    const classes = classNames('ReactCrop__container', {
        [styles.animate]: animateControls
    });

    const minimalSize = Math.min(imageSize.width, imageSize.height) / 4;

    if (!crop) {
        return null;
    }

    return (
        <ReactCrop
            className={classes}
            crop={crop}
            aspect={aspectRatio}
            ruleOfThirds
            keepSelection
            onDragStart={() => setAnimateControls(false)}
            onChange={setCrop}
            minHeight={minimalSize}
            minWidth={minimalSize}
            onComplete={updateCropBox}
            renderSelectionAddon={() => (
                <CropOverlay
                    filter={filter}
                    url={overlayUrl}
                    crop={crop!} clip={clip}
                    containerSize={containerSize} />
            )}>
            <div className={styles.children}>
                {children}
            </div>
        </ReactCrop>
    );
};

type Props = PropsWithChildren<{
    overlayUrl: string;
    onChange: (cropBox: Area) => void;
    imageSize: Size;
    activeCropBox: Area;
    containerSize: Size | undefined;
    clip: Clip | undefined;
    aspectRatio?: number;
    filter: string | null;
}>;